/**
 * Helpers for the Robi Quick Answer (WlbHelpAgent) conversation.
 *
 * SendMessage answers this conversation type with an envelope carrying both
 * the echoed user message and Robi's reply, unlike human threads which answer
 * with a bare message DTO. Both shapes are handled here so neither platform
 * has to guess.
 */
import type { ImageSource } from '../../types/common';
import type { ExtendedChatMessage } from '../../types/chat';
import type { ConversationMessageDto } from './conversationTypes';
import {
  generateLocalMessageId,
  mapMessageDto,
  resolveMessageId,
} from './conversationMapper';

type SendMessageResponseEnvelope = {
  message?: ConversationMessageDto;
  aiMessage?: ConversationMessageDto;
};

export interface ParsedSendMessageResponse {
  userMessage?: ConversationMessageDto;
  aiMessage?: ConversationMessageDto;
}

/** Identity used to render Robi's bubbles; the UI layer owns the avatar. */
export interface QuickAnswerAgent {
  _id: string | number;
  name: string;
  avatar?: ImageSource | string | null;
}

export const parseSendMessageResponse = (
  data: unknown,
): ParsedSendMessageResponse => {
  if (!data || typeof data !== 'object') return {};
  const record = data as SendMessageResponseEnvelope & ConversationMessageDto;
  if ('message' in record || 'aiMessage' in record) {
    return { userMessage: record.message, aiMessage: record.aiMessage };
  }
  return { userMessage: record };
};

export const mapQuickAnswerAiMessage = (
  aiMessage: ConversationMessageDto,
  agent: QuickAnswerAgent,
): ExtendedChatMessage => {
  const mapped = mapMessageDto(aiMessage);
  const messageId = resolveMessageId(aiMessage);

  return {
    ...mapped,
    _id: messageId ?? generateLocalMessageId(),
    text: aiMessage.bodyText?.trim() ?? '',
    createdAt: mapped.createdAt ?? new Date(),
    user: {
      _id: agent._id,
      name: aiMessage.senderUserName?.trim() || agent.name,
      avatar: agent.avatar ?? null,
    },
    isAgentMessage: true,
    type: aiMessage.messageType ?? 'AiResponse',
  };
};

/**
 * Robi's reply can carry a timestamp equal to or older than the message that
 * triggered it, which would sort it above the user's own bubble.
 */
export const ensureReplyAfter = (
  reply: ExtendedChatMessage,
  previous: ExtendedChatMessage | undefined,
): ExtendedChatMessage => {
  if (!previous) return reply;
  const previousTime = new Date(previous.createdAt).getTime();
  const replyTime = new Date(reply.createdAt).getTime();
  if (Number.isFinite(replyTime) && replyTime > previousTime) return reply;
  return { ...reply, createdAt: new Date(previousTime + 1) };
};
